import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  LocalHospital as HospitalIcon,
  Shield as ShieldIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SparklesCore } from '../../components/SparklesCore';
import { ContainerTextFlip } from '../../components/ContainerTextFlip';
import { BRAND_LOGO_LOGIN } from '../../config/brandAssets';
import AuthLoadingScreen from '../../components/AuthLoadingScreen';
import { useAuthScreenViewport } from '../../utils/useAuthScreenViewport';
import { isKlinviaNativeApp, openNativeLauncher } from '../../utils/nativeApp';

const Login: React.FC = () => {
  useAuthScreenViewport({ darkBackground: true });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  
  // Setup fields
  const [setupName, setSetupName] = useState('');
  const [setupKey, setSetupKey] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [twoFactorChallenge, setTwoFactorChallenge] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  
  const navigate = useNavigate();
  const { login, verifyTwoFactor, setup, isAuthenticated, needsSetup, isLoading, defaultRoute } = useAuth();

  // Redirecionar se já autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate(defaultRoute, { replace: true });
    }
  }, [isAuthenticated, navigate, defaultRoute]);

  // No app iOS o login é a tela nativa (escolha da clínica + Face ID).
  const nativeApp = isKlinviaNativeApp();
  useEffect(() => {
    if (nativeApp && !isLoading && !isAuthenticated) openNativeLauncher();
  }, [nativeApp, isLoading, isAuthenticated]);

  // Ativar modo setup se necessário
  useEffect(() => {
    if (needsSetup) {
      setIsSetupMode(true);
    }
  }, [needsSetup]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email.trim() || !password) {
        setError('Preencha todos os campos.');
        setLoading(false);
        return;
      }
      const result = await login(email.trim(), password);
      if (result.requiresTwoFactor) {
        setTwoFactorChallenge(result.challengeToken);
        setTwoFactorCode('');
      }
      // Sem 2FA, o useEffect acima irá redirecionar
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorChallenge) return;
    setError('');

    if (twoFactorCode.length !== 6) {
      setError('Digite os 6 dígitos do aplicativo autenticador.');
      return;
    }

    setLoading(true);
    try {
      await verifyTwoFactor(twoFactorChallenge, twoFactorCode);
    } catch (err: any) {
      const message = err.message || 'Código de verificação inválido.';
      setError(message);
      setTwoFactorCode('');
      if (/expirad|bloquead|novamente/i.test(message)) setTwoFactorChallenge(null);
    } finally {
      setLoading(false);
    }
  };

  const cancelTwoFactor = () => {
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    setPassword('');
    setError('');
  };

  const isTwoFactorStep = !isSetupMode && Boolean(twoFactorChallenge);

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email.trim() || !password || !setupName.trim() || !setupKey.trim()) {
        setError('Preencha todos os campos.');
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError('As senhas não coincidem.');
        setLoading(false);
        return;
      }

      if (password.length < 6) {
        setError('A senha deve ter pelo menos 6 caracteres.');
        setLoading(false);
        return;
      }

      await setup({
        email: email.trim(),
        password,
        full_name: setupName.trim(),
        setup_key: setupKey.trim(),
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao realizar setup.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || nativeApp) {
    return <AuthLoadingScreen variant="dark" />;
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
      '&:hover fieldset': { borderColor: 'rgba(59, 130, 246, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' },
      '& input': {
        color: '#ffffff',
        '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)', opacity: 1 },
      },
    },
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f172a',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      {/* Partículas */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
        <SparklesCore
          background="transparent"
          minSize={0.6}
          maxSize={1.4}
          speed={2}
          particleColor="#3b82f6"
          particleDensity={60}
          className="h-full w-full"
          fitParent
        />
      </Box>

      {/* Overlay */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.02)', backdropFilter: 'blur(1px)', zIndex: 3, pointerEvents: 'none' }} />

      {/* Container Principal */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: '1200px' },
          position: 'relative',
          zIndex: 100,
          px: { xs: 2, sm: 3 },
          py: { xs: 4, sm: 6 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Card de Login/Setup */}
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '480px' }, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'rgba(15, 23, 42, 0.8)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              overflow: 'hidden',
            }}
          >
            {/* Header com Logo */}
            <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', borderBottom: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Box
                  component="img"
                  src={BRAND_LOGO_LOGIN}
                  alt="Logo"
                  sx={{
                    height: 'auto',
                    maxHeight: { xs: '100px', sm: '130px', md: '150px' },
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 8px 24px rgba(59, 130, 246, 0.3))',
                  }}
                />
              </Box>
              
              {isSetupMode ? (
                <>
                  <Chip
                    icon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                    label="Configuração Inicial"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.15)',
                      color: '#3b82f6',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      mb: 1,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                    Crie a conta do administrador para começar
                  </Typography>
                </>
              ) : isTwoFactorStep ? (
                <>
                  <Chip
                    icon={<ShieldIcon sx={{ fontSize: 16 }} />}
                    label="Verificação em duas etapas"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(20, 184, 166, 0.14)',
                      color: '#5eead4',
                      border: '1px solid rgba(20, 184, 166, 0.35)',
                      mb: 1,
                      '& .MuiChip-icon': { color: '#5eead4' },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                    Abra o app autenticador (Google Authenticator, Authy…) e digite o código do Klinvia
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                  Acesse sua conta para continuar
                </Typography>
              )}
            </Box>

            {/* Formulário */}
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              <form onSubmit={isSetupMode ? handleSetup : isTwoFactorStep ? handleVerifyTwoFactor : handleLogin}>
                {isTwoFactorStep && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                      Código de 6 dígitos
                    </Typography>
                    <TextField
                      fullWidth
                      autoFocus
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      inputProps={{
                        inputMode: 'numeric',
                        autoComplete: 'one-time-code',
                        maxLength: 6,
                        'aria-label': 'Código do aplicativo autenticador',
                        style: { letterSpacing: '0.5em', fontSize: '1.4rem', fontWeight: 600, textAlign: 'center' },
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><ShieldIcon sx={{ color: 'rgba(20, 184, 166, 0.7)' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.75, display: 'block' }}>
                      Entrando como {email.trim()}. O código muda a cada 30 segundos.
                    </Typography>
                  </Box>
                )}

                {!isTwoFactorStep && (<>
                {/* Campo Nome (apenas setup) */}
                {isSetupMode && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                      Nome Completo
                    </Typography>
                    <TextField
                      fullWidth
                      value={setupName}
                      onChange={(e) => setSetupName(e.target.value)}
                      placeholder="Seu nome completo"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: 'rgba(59, 130, 246, 0.6)' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Box>
                )}

                {/* Campo Email */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    autoFocus={!isSetupMode}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: 'rgba(59, 130, 246, 0.6)' }} /></InputAdornment>,
                    }}
                    sx={inputSx}
                  />
                </Box>

                {/* Campo Senha */}
                <Box sx={{ mb: isSetupMode ? 2.5 : 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                    Senha
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSetupMode ? 'Crie uma senha forte' : 'Digite sua senha'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: 'rgba(59, 130, 246, 0.6)' }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#3b82f6' } }}>
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Box>

                {/* Confirmar Senha (apenas setup) */}
                {isSetupMode && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                      Confirmar Senha
                    </Typography>
                    <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repita a senha"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: 'rgba(59, 130, 246, 0.6)' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Box>
                )}

                {/* Chave de Setup (apenas setup) */}
                {isSetupMode && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.85rem' }}>
                      Chave de Ativação
                    </Typography>
                    <TextField
                      fullWidth
                      type="password"
                      value={setupKey}
                      onChange={(e) => setSetupKey(e.target.value)}
                      placeholder="Chave fornecida pelo sistema"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><VpnKeyIcon sx={{ color: 'rgba(59, 130, 246, 0.6)' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
                      Use a SETUP_KEY ou JWT_SECRET do servidor como chave de ativação
                    </Typography>
                  </Box>
                )}
                </>)}

                {/* Erro */}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2.5,
                      borderRadius: 2,
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#fca5a5',
                      '& .MuiAlert-icon': { color: '#ef4444' },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Botão */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : isSetupMode ? <PersonAddIcon /> : isTwoFactorStep ? <ShieldIcon /> : <LoginIcon />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 8px 25px rgba(59, 130, 246, 0.35)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      boxShadow: '0 12px 35px rgba(59, 130, 246, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': { transform: 'translateY(0)' },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  {loading ? 'Processando...' : isSetupMode ? 'Criar Conta e Entrar' : isTwoFactorStep ? 'Verificar e entrar' : 'Entrar'}
                </Button>

                {isTwoFactorStep && (
                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={cancelTwoFactor}
                    disabled={loading}
                    sx={{ mt: 1.5, color: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { color: '#3b82f6' } }}
                  >
                    Voltar e usar outra conta
                  </Button>
                )}
              </form>

              {/* Toggle setup/login */}
              {needsSetup && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => { setIsSetupMode(!isSetupMode); setError(''); }}
                    sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { color: '#3b82f6' } }}
                  >
                    {isSetupMode ? 'Já tenho conta — Fazer login' : 'Primeira vez? Configurar sistema'}
                  </Button>
                </Box>
              )}

              {/* Rodapé */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)', textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <HospitalIcon sx={{ fontSize: 14, color: 'rgba(59, 130, 246, 0.5)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                    Sistema seguro para clínicas de saúde
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Seção de Texto - Oculto no mobile */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: '500px' },
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 700,
              fontSize: { md: '2.5rem', lg: '3rem' },
              lineHeight: 1.2,
            }}
          >
            Gestão Completa
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start', width: '100%', '& .text-white': { color: 'white !important' } }}>
            <ContainerTextFlip
              words={['para sua Clínica', 'para seu Consultório', 'para sua Equipe', 'para seus Pacientes']}
              typingSpeed={80}
              deleteDelay={3000}
              deleteSpeed={40}
              className="!text-white dark:!text-white"
              textClassName="!text-white dark:!text-white"
            />
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              maxWidth: '450px',
              mt: 2,
            }}
          >
            Agenda, prontuário, receituários, orçamentos, controle financeiro e muito mais. 
            Tudo em um único sistema profissional para clínicas médicas e odontológicas.
          </Typography>

          {/* Badges de funcionalidades */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
            {['Agenda', 'Prontuário', 'Receituários', 'Orçamentos', 'Financeiro', 'Relatórios'].map((feature) => (
              <Chip
                key={feature}
                label={feature}
                size="small"
                sx={{
                  bgcolor: 'rgba(59, 130, 246, 0.1)',
                  color: '#5eead4',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
